import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import Box from '@material-ui/core/Box';
import MaterialTable from 'material-table';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import Button from '@material-ui/core/Button';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import Search from '@material-ui/icons/Search';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import useDebounce from '../global/use-debounce';
import { getMalls } from '../../actions/admin/mall';
import { getCategories } from '../../actions/admin/category';
import { getPaginatePages, deletePage } from '../../actions/admin/pageAction';
import { updateModalContent, toggleModal } from '../../actions/modal';
import { IsEmpty, IsTrue } from '../../common/checks';
import Constants from '../../common/constants';
import Images from '../basics/Images';

const PageTable = (props) => {
  const {
    store: { updatedAt },
  } = props;
  const table = useRef(null);

  const initializeFilterValue = {
    category:          null,
    mall:              null,
    promotion:         null,
    promotionCategory: null,
  };

  const initializeFocusValue = {
    searchFocus: false,
    nameFocus:   false,
  };

  const [pageSize, setPageSize] = useState(20);
  const [autoFocus, setAutoFocus] = useState(initializeFocusValue);
  const [filterData, setFilterData] = useState(initializeFilterValue);

  const [currentOrder, setCurrentOrder] = useState('');
  const [id, setId] = useState('');
  const [search, setSearch] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');

  const debounceId = useDebounce(id, 500);
  const debouncedSearch = useDebounce(search, 500);
  const debouncedName = useDebounce(name, 500);
  const { idFocus, searchFocus } = autoFocus;

  const [onResetId, setOnResetId] = useState(false);
  const [onResetSearch, setOnResetSearch] = useState(false);
  const [onResetName, setOnResetName] = useState(false);

  const columns = [
    { title: 'ID', field: 'id', defaultSort: 'asc' },
    // {
    //     title: "Logo",
    //     field: "logo",
    //     render: page => {
    //         if (page.logo)
    //           return <img src={`${page.logo}`} width="50px" alt="" />;
    //     }
    // },
    {
      title:         'Name',
      field:         'name',
      defaultFilter: name,
      render:        (page) => (
        <>
          <Link target="_blank" to={`/admin/page/${page.id}`}>
            {page.name}
          </Link>

          {
            IsTrue(page.isOfficial) && (
              <img src={Images.Local.Authorised} width="20px" alt="isOfficial" className="ml-3" />
            )
          }
        </>
      ),
    },
    { title: 'Type', field: 'type' },
    {
      title: 'Status',
      field: 'status',
    },
  ];

  useEffect(() => {
    table.current && table.current.onQueryChange();
  }, [filterData, updatedAt]);

  useEffect(() => {
    if (!onResetSearch) {
      table.current && table.current.onQueryChange();
    } else {
      setOnResetSearch(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!onResetName) {
      table.current && table.current.onQueryChange();
    } else {
      setOnResetName(false);
    }
  }, [debouncedName]);

  useEffect(() => {
    if (!onResetId) {
      table.current && table.current.onQueryChange();
    } else {
      setOnResetId(false);
    }
  }, [debounceId]);

  useEffect(() => {
    table.current && table.current.onQueryChange();
  }, [type, status]);

  function handleSort() {
    if (currentOrder) {
      if (currentOrder === 'DESC') {
        setCurrentOrder('ASC');
      } else {
        setCurrentOrder('DESC');
      }
    } else {
      setCurrentOrder('DESC');
    }
  }

  const handleIdChange = (e) => {
    setId(e.target.value);

    setAutoFocus({ idFocus: true, nameFocus: false, searchFocus: false });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);

    setAutoFocus({ nameFocus: false, searchFocus: true });
  };

  const onDeletePage = (id) => {
    props.updateModalContent({
      title:        'Are you sure?',
      content:      'All data related to this page will be deleted!',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     () => props.deletePage(id),
      },
      cancelText:       'No',
      closeAfterAction: true,
    });

    props.toggleModal();
  };

  const onResetFilter = () => {
    setFilterData(initializeFilterValue);
    setOnResetSearch(true);
    setOnResetName(true);
    setOnResetId(true);
    setId('');
    setSearch('');
    setName('');
    setType('');
    setStatus('');
    setAutoFocus(initializeFocusValue);
  };

  return (
    <MaterialTable
      tableRef={table}
      className="material-table"
      title=""
      columns={columns}
      onChangeRowsPerPage={(pageSize) => {
        setCurrentOrder('');
        setAutoFocus(initializeFocusValue);
        setPageSize(pageSize);
      }}
      icons={{
        SortArrow: () => (currentOrder === '' ? <UnfoldMoreIcon onClick={handleSort} />
          : currentOrder === 'ASC' ? <ArrowUpwardIcon onClick={handleSort} />
            : <ArrowDownwardIcon onClick={handleSort} />),
      }}
      data={(query) => new Promise(async (resolve, reject) => {
        try {
          query.id = id;
          query.name = search;
          query.types = IsEmpty(type) ? [] : [type];
          query.status = status;

          if (!IsEmpty(query.orderBy)) {
            query.order = { col: query.orderBy.field, dir: currentOrder };
          }
          // The MaterialTable has an issue, if press sort 3x, the 3rd value of orderBy will be undefined, so for preventing that one, in this condition we only set the dir value.
          if (!IsEmpty(query.order)) {
            query.order.dir = currentOrder;
          }
          const result = await props.getPaginatePages(query);

          let { rows, count } = result;
          let { page } = query;

          if (rows.length === 0) {
            page = 0;

            if (count !== 0) {
              const resultFirstPage = await props.getPaginatePages({ ...query, page });
              rows = resultFirstPage.rows;
            }
          }

          resolve({
            data:       rows,
            page,
            totalCount: count,
          });
        } catch (err) {
          resolve({
            data:       [],
            page:       0,
            totalCount: 0,
          });
        }
      })}
      actions={[
        {
          icon:    'edit',
          tooltip: 'View/edit Page',
          onClick: (event, page) => {
            window.open(`/admin/page/${page.id}`, '_blank');
          },
        },
        {
          icon:    'delete',
          tooltip: 'Delete Page',
          onClick: (event, page) => onDeletePage(page.id),
        },
      ]}
      options={{
        actionsColumnIndex:  -1,
        pageSize,
        emptyRowsWhenPaging: false,
        filtering:           false,
        search:              false,
      }}
      components={{
        Toolbar: () => (
          <div className="pt-3">
            <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
              <Box p={1} width="15%">
                <FormControl fullWidth>
                  <Button variant="outlined" size="medium" color="secondary" onClick={onResetFilter}>Clear Filters</Button>
                </FormControl>
              </Box>
            </Box>
            <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
              <Box p={1} width="10%">
                <FormControl fullWidth>
                  <TextField
                    label="Id"
                    type="number"
                    value={id}
                    onChange={handleIdChange}
                    autoFocus={idFocus}
                  />
                </FormControl>
              </Box>
              <Box p={1} width="23%">
                <FormControl fullWidth>
                  <TextField
                    label="Page Name"
                    id="standard-adornment-amount"
                    placeholder="Search by name"
                    value={search}
                    onChange={handleSearchChange}
                    type="search"
                    startAdornment={(
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    )}
                    autoFocus={searchFocus}
                  />
                </FormControl>
              </Box>
              <Box p={1} width="25%">
                <FormControl fullWidth>
                  <InputLabel id="status-filter-label">Type</InputLabel>
                  <Select
                    labelId="type-filter-label"
                    name="pageType"
                    value={type}
                    onChange={(e) => { setType(e.target.value); }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={Constants.PageType.FoodReview}>Food Review</MenuItem>
                    <MenuItem value={Constants.PageType.Retailer}>Retailer</MenuItem>
                    <MenuItem value={Constants.PageType.ECommerce}>E-Commerce</MenuItem>
                    <MenuItem value={Constants.PageType.EventsCompany}>Events Company</MenuItem>
                    <MenuItem value={Constants.PageType.View}>View</MenuItem>
                    <MenuItem value={Constants.PageType.Mall}>Mall</MenuItem>
                    <MenuItem value={Constants.PageType.FNB}>F&#38;B</MenuItem>
                    <MenuItem value={Constants.PageType.CreditCard}>Credit Card</MenuItem>
                    <MenuItem value={Constants.PageType.HawkerCentre}>Hawker Centre</MenuItem>
                    <MenuItem value={Constants.PageType.AreaManagement}>Area Management</MenuItem>
                    <MenuItem value={Constants.PageType.Photographer}>Photographer</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box p={1} width="20%">
                <FormControl fullWidth>
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    name="pageStatus"
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={Constants.Status.Pending}>Pending</MenuItem>
                    <MenuItem value={Constants.Status.Live}>Live</MenuItem>
                    <MenuItem value={Constants.Status.Rejected}>Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </div>
        ),
      }}
    />
  );
};

PageTable.propTypes = {
  store: PropTypes.shape({
    updatedAt: PropTypes.string,
  }).isRequired,
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
  deletePage:         PropTypes.func.isRequired,
  getPaginatePages:   PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  store: state.admin.page,
});

export default connect(mapStateToProps, {
  deletePage,
  updateModalContent,
  toggleModal,
  getMalls,
  getCategories,
  getPaginatePages,
})(PageTable);
